// LiveTutorProvider.tsx

import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useRef,
} from "react";
import { Canvas } from "@react-three/fiber";
import { Character3D } from "../components/livetutor/character/Character3D";
import { BlendShapeProvider } from "./BlendShapeProvider";
import { FaExclamation } from "react-icons/fa";
import {
	MdClose,
	MdChat,
	MdSettings,
	MdSend,
	MdStop,
	MdMic,
	MdError,
	MdVisibility,
	MdVisibilityOff,
	MdDelete,
	MdArrowForward,
} from "react-icons/md";
import LiveTutorSettingsModal from "../pages/livetutor/LiveTutorSettings";
import { useStorage } from "./useStorage";
import { LiveTutorSettings, Message, RecognitionCallbacks } from "../types";
import { AudioRecorder } from "../utils/AudioRecorder";
import { useServer } from "./useServer";
import { useAzure } from "./useAzure";
import { split } from "sentence-splitter";
import { reduceByRole, convertPronunciationToString } from "../utils/Helpers";
import P from "../helper/P";
import { useTour } from "@reactour/tour";
import { useToast } from "./ToastProvider";

import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { steps } from "framer-motion";
import { useHotStreak } from "./HotStreakProvider";

interface LiveTutorContextType {
	isHovered: boolean;
	setIsHovered: React.Dispatch<React.SetStateAction<boolean>>;
	showMessageLog: boolean;
	setShowMessageLog: React.Dispatch<React.SetStateAction<boolean>>;
	showSettings: boolean;
	setShowSettings: React.Dispatch<React.SetStateAction<boolean>>;

	suggestion: string | null;
	setSuggestion: React.Dispatch<React.SetStateAction<string | null>>;
	show: boolean;
	setShow: React.Dispatch<React.SetStateAction<boolean>>;

	speechState: {
		messageQueue: {
			content: string;
			message_type?: string;
			voice_id?: string;
		}[];
		currentSpeechIndex: number;
	};

	setSpeechState: React.Dispatch<
		React.SetStateAction<{
			messageQueue: {
				content: string;
				message_type?: string;
				voice_id?: string;
			}[];
			currentSpeechIndex: number;
		}>
	>;

	messages: { role: string; content: string }[];
	setMessages: React.Dispatch<
		React.SetStateAction<{ role: string; content: string }[]>
	>;
	liveTutorSettings: any;
	setLiveTutorSettings: React.Dispatch<React.SetStateAction<any>>;
	handleSuggestion: () => void;
	handleMessageQueue: () => void;
	handleChat: (
		message: Message | string,
		add_to_log?: boolean,
		pronunciation?: any
	) => Promise<string | null>;
	systemMessageRef: React.MutableRefObject<string | null>;
	handleSpeak: (
		message: string,
		message_type?: string,
		voice_id?: string
	) => Promise<void>;

	userMessageCallbackRef: React.MutableRefObject<any>;
	messageCallbackRef: React.MutableRefObject<any>;
	callbackRef: React.MutableRefObject<any>;
	stepFinishCallbackRef: React.MutableRefObject<() => void>;
	finishCallbackRef: React.MutableRefObject<((message: string) => void) | null>;
	pronunciationResultsRef: React.MutableRefObject<any>;

	completed: any;

	actionHandler: React.MutableRefObject<any>;
	abortControllerRef: React.MutableRefObject<AbortController>;

	infoHandlerRef: React.MutableRefObject<any>;
	currentState: any;
	setCurrentState: React.Dispatch<React.SetStateAction<any>>;
	lessonInfoRef: React.MutableRefObject<any>;
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;

	exerciseInfoRef: React.MutableRefObject<any>;
	activeStates: React.MutableRefObject<any>;

	showContinueButton: boolean;
	setShowContinueButton: React.Dispatch<React.SetStateAction<boolean>>;

	addActiveState: (state: string) => void;
	removeActiveState: (state: string) => void;

	resetLessons: () => void;
}

const LiveTutorContext = createContext<LiveTutorContextType | undefined>(
	undefined
);

export const useLiveTutor = () => {
	const context = useContext(LiveTutorContext);
	if (!context) {
		throw new Error("useLiveTutor must be used within a LiveTutorProvider");
	}
	return context;
};

interface LiveTutorProviderProps {
	children: React.ReactNode;
}

export const LiveTutorProvider: React.FC<LiveTutorProviderProps> = ({
	children,
}) => {
	//const { isOpen, currentStep, setDisabledActions } = useTour();
	const { showToast } = useToast();

	const { useLocalStorage } = useStorage();

	const { stopTextAsync, speakTextAsync, isSpeakingRef } = useAzure();
	const {
		smartChat,
		chat,
		transcribe,
		user,
		translate,
		checkStatus,
		updateUser,
		finishLesson,
		addXp,
		checkStreak,
	} = useServer();
	const { showHotStreak } = useHotStreak();

	const [show, setShow] = useState(true);
	const [isHovered, setIsHovered] = useState(false);
	const [showMessageLog, setShowMessageLog] = useState(false);
	const [showSettings, setShowSettings] = useState(false);

	const [suggestion, setSuggestion] = useState<string | null>(null);

	const [speechState, setSpeechState] = useState({
		messageQueue: [] as {
			content: string;
			message_type?: string;
			voice_id?: string;
		}[],
		currentSpeechIndex: 0,
	});
	const abortControllerRef = useRef<AbortController>(new AbortController());

	const lessonInfoRef = useRef<any>({
		type: "start",
		steps: [
			{
				type: "start",
			},
		],
	});

	const [step, setStep] = useState(0);
	const [currentState, setCurrentState] = useState<
		| "onboarding"
		| "start"
		| "general_lesson"
		| "general_lesson_by_interest"
		| "pronunciation_lesson"
		| "roleplay_lesson"
		| "conversation_lesson"
	>("start");

	const activeStates = useRef<any>([]);

	// Callbacks
	const userMessageCallbackRef = useRef<(message: string) => void>(() => {}); // Sends the raw message sent by the user
	const messageCallbackRef = useRef<((message: string) => void) | null>(null); // Sends the raw message sent by the assistant before they speak
	const callbackRef = useRef<((message: string) => void) | null>(null); // Sends the message as it is spoken by the assistant
	const stepFinishCallbackRef = useRef<() => Promise<void>>(async () => {}); // Called when the step is finished
	const finishCallbackRef = useRef<((message: string) => void) | null>(null); // Called when the lesson is finished

	const pronunciationResultsRef = useRef<any>({});

	const [completed, setCompleted] = useLocalStorage<any>("completed", {});

	const exerciseInfoRef = useRef<any>({});
	const [showContinueButton, setShowContinueButton] = useState(false);
	//const stateRef = useRef<any>(null);

	const infoHandlerRef = useRef<any>(null);
	const [auxilliaryStack, setAuxilliaryStack] = useState<any[]>([]);

	// Helper function to mark a lesson as completed
	const markLessonAsCompleted = (lessonType: string) => {
		const today = new Date().toLocaleDateString();
		setCompleted((prev: any) => {
			return {
				[today]: [...(prev[today] || []), lessonType],
			};
		});
		setCurrentState("start");
	};

	const resetLessons = () => {
		setCompleted({});
	};

	const continueHandler = () => {
		console.log("Continue handler");
		setShowContinueButton(false);
		if (auxilliaryStack.length > 0) {
			console.log("Auxilliary stack", auxilliaryStack);
			auxilliaryStack.pop();
		} else if (step < lessonInfoRef.current.steps.length - 1) {
			console.log("Next handler");
			// increment step
			setStep((prev) => prev + 1);
			exerciseInfoRef.current = lessonInfoRef.current.steps[step];
		} else {
			// finish and set to start (for now)
			console.log("Finish handler", currentState);
			markLessonAsCompleted(currentState);
			addXp(50);

			finishCallbackRef.current && finishCallbackRef.current(currentState);
			finishCallbackRef.current = null;
			if (lessonInfoRef.current.type !== "onboarding") {
				finishLesson(lessonInfoRef.current, messagesRef.current);
			}
			setStep(0);
			setCurrentState("start");
			lessonInfoRef.current = {
				type: "start",
				steps: [
					{
						type: "start",
					},
				],
			};
		}
	};

	const actionHandler = useRef<any>({
		continue: async () => {
			// show the button
			stepFinishCallbackRef.current && (await stepFinishCallbackRef.current());
			setShowContinueButton(true);
		},
		spell_word: ({ word }: { word: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "spell_word", word },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		pronounce: ({ text }: { text: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "pronounce", text },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		show_image: ({ alt }: { alt: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "show_image", alt },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		show_word: ({ word }: { word: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "show_word", word },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		use_word_in_sentence: ({ word }: { word: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "use_word_in_sentence", word },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		describe_image: ({ word, alt }: { word: string; alt: string }) => {
			const auxilliaryStackCopy = [
				...auxilliaryStack,
				{ name: "describe_image", word, alt },
			];
			setAuxilliaryStack(auxilliaryStackCopy);
		},
		do_nothing: () => {},
		roleplay: ({ text }: { text: string }) => {},
	});

	const parseAndExecuteAction = (actionString: string) => {
		// Regular expression to match function name and arguments
		const regex = /(\w+)\((.*?)\)/;
		const match = actionString.match(regex);

		if (match) {
			const [_, functionName, argument] = match;

			// Check if the function exists in actionHandler
			if (actionHandler.current[functionName]) {
				// Call the function with the appropriate argument structure
				actionHandler.current[functionName]({
					// Determine the parameter name based on the function
					[getFunctionParamName(functionName)]: argument,
				});
			}
		}
	};

	// Helper function to determine the parameter name based on the function
	const getFunctionParamName = (functionName: string): string => {
		switch (functionName) {
			case "spell_word":
			case "show_word":
			case "use_word_in_sentence":
				return "word";
			case "pronounce":
			case "roleplay":
				return "text";
			case "show_image":
				return "alt";
			default:
				return "";
		}
	};

	const [messages, setMessages] = useState<{ role: string; content: string }[]>(
		[]
	);
	const [liveTutorSettings, setLiveTutorSettings] =
		useLocalStorage<LiveTutorSettings>("liveTutorSettings", {
			volume: 100,
			liveConversation: false,
			autoPlayMessages: true,
			speakingRate: -20,
			nativeLanguage: false,
		});

	const messagesRef = useRef<{ role: string; content: string }[]>(messages);

	const addActiveState = (state: string) => {
		if (activeStates.current.includes(state)) return;
		activeStates.current = [...activeStates.current, state];
	};

	const removeActiveState = (state: string) => {
		activeStates.current = activeStates.current.filter(
			(activeState: string) => activeState !== state
		);
	};

	const nextSpeechTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const systemMessageRef = useRef<
		string | null
	>(`You are a friendly AI tutor named Sarah. You are teaching English to beginners.
Keep your responses short and simple.`);

	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	useEffect(() => {
		if (!currentState) return;
		(async () => {
			//if (currentState !== "start" && currentState !== "onboarding" && await checkStreak()) {
			showHotStreak();
			//}
		})();
		exerciseInfoRef.current = lessonInfoRef.current.steps[step];
		messagesRef.current = [
			...messagesRef.current,
			{
				role: "system",
				content: `Current step has changed to ${currentState}`,
			},
		];
		setMessages(messagesRef.current);

		//handleChat("Read the instruction/question.", false);
	}, [currentState, step]);

	/*useEffect(() => {
		console.log("Current step", currentStep, isOpen);
		if (isOpen) {
			setTimeout(() => {
				console.log("Current step", currentStep);
				switch (currentStep) {
					case 0:
						handleSpeak("Hi, Welcome to Fluent Future. I will be helping you improve your English speaking skills.");
						break;
					case 1:
						handleSpeak(`Click on the text to translate it to ${user.nativeLanguage}.`);
						break;
					case 2:
						handleSpeak(`Great! You can talk to me in ${user.nativeLanguage} or English. Go ahead and ask me a question by clicking on the microphone.`);
						break;
					case 3:
						handleSpeak("You can also open the chat log and type your questions there.");
						break;
					default:
						break;
				}
			}, currentStep === 0 ? 2000 : 200);
		}
	}, [currentStep, isOpen]);*/

	useEffect(() => {
		const { currentSpeechIndex, messageQueue } = speechState;
		if (currentSpeechIndex >= 0 && currentSpeechIndex < messageQueue.length) {
			const currentSentence = messageQueue[currentSpeechIndex].content;

			const newMessages = [
				...messagesRef.current,
				{
					role:
						messageQueue[currentSpeechIndex].message_type === "roleplay"
							? "system"
							: "assistant",
					content: currentSentence,
				},
			] as Message[];

			setMessages(newMessages);

			if (isSpeakingRef.current) {
				return;
			}

			nextSpeechTimeoutRef.current &&
				clearTimeout(nextSpeechTimeoutRef.current);

			// stopTextAsync();

			console.log("Speaking:", messageQueue[currentSpeechIndex]);
			messageCallbackRef.current && messageCallbackRef.current(currentSentence);

			// Speak the current sentence
			speakTextAsync({
				text: currentSentence,
				rate: liveTutorSettings.speakingRate,
				voice: messageQueue[currentSpeechIndex].voice_id || "en-US-AvaNeural",
				updateBlendShapes: messageQueue[currentSpeechIndex].voice_id
					? false
					: true,
				cb: () => {
					nextSpeechTimeoutRef.current = setTimeout(() => {
						setSpeechState((prevState) => {
							const { currentSpeechIndex, messageQueue } = prevState;

							if (currentSpeechIndex < messageQueue.length - 1) {
								return {
									...prevState,
									currentSpeechIndex: currentSpeechIndex + 1,
								};
							} else if (currentSpeechIndex === messageQueue.length - 1) {
								return {
									messageQueue: [],
									currentSpeechIndex: 0,
								};

								/*setSpeechState({
									messageQueue: [],
									currentSpeechIndex: -1
								});*/
							} else {
								return prevState;
							}
						});
					}, 400);

					callbackRef.current && callbackRef.current(currentSentence);
				},
			});
		}
	}, [speechState.currentSpeechIndex, speechState.messageQueue]);

	const handleSuggestion = async () => {
		if (suggestion) {
			handleSpeak(suggestion);
			setSuggestion(null);
		}
	};

	const handleSpeak = async (
		message: string,
		message_type: string = "",
		voice_id: string = ""
	) => {
		if (message_type) {
			const sentence_objs = [
				{
					content: message,
					message_type,
					voice_id,
				},
			];

			setSpeechState((prevState) => ({
				messageQueue: [...prevState.messageQueue, ...sentence_objs],
				currentSpeechIndex: prevState.currentSpeechIndex,
			}));

			return;
		}

		const sentences = split(message)
			.map((sentence) => sentence.raw)
			.filter((sentence) => sentence.trim().length > 0);
		const sentence_objs = sentences.map((sentence) => ({ content: sentence }));

		setSpeechState((prevState) => ({
			messageQueue: [...prevState.messageQueue, ...sentence_objs],
			currentSpeechIndex: prevState.currentSpeechIndex,
		}));
	};

	const handleMessageQueue = async () => {
		console.log("Handling message queue");
		setSpeechState((prevState) => {
			const { currentSpeechIndex, messageQueue } = prevState;

			if (currentSpeechIndex < messageQueue.length - 1) {
				return {
					...prevState,
					currentSpeechIndex: currentSpeechIndex + 1,
				};
			}

			return prevState; // No change if none of the conditions are met
		});
	};

	const clearMessageQueue = () => {
		setSpeechState({
			messageQueue: [],
			currentSpeechIndex: -1,
		});
	};

	const handleChat = async (
		message: Message | string,
		add_to_log: boolean = true,
		pronunciation: any = null
	): Promise<string | null> => {
		// check if message is string
		if (typeof message === "string") {
			message = { role: "user", content: message } as Message;
			if (add_to_log) {
				// then we need to make sure its in english
				const { text, language } = await translate(
					message.content,
					"auto",
					"en"
				);
				if (language !== "en") {
					message.translated = text;
					message.language = language;
				}
			}
		}

		let newMessages = [...messagesRef.current] as Message[];

		if (message) {
			// Add pronunciation to the message if it exists
			console.log("pronunciation", pronunciation);
			if (pronunciation) {
				message.pronunciation = pronunciation.pronunciation_analysis;
			}

			newMessages = [...messagesRef.current, message] as Message[];
			if (add_to_log) {
				userMessageCallbackRef.current &&
					userMessageCallbackRef.current(message.content);
				setMessages(newMessages);
			} else {
				// Add the message queue messages to the new messages
				newMessages = [
					...newMessages,
					...speechState.messageQueue.map((message) => ({
						role: "assistant",
						content: message.content,
					})),
				] as Message[];
			}
		}

		// reduce
		const reducedMessages = reduceByRole(newMessages);

		// Send chat message
		const messagesToSend = [...reducedMessages.slice(-10)];

		const exercise_info = {
			...lessonInfoRef.current.steps[step],
			...exerciseInfoRef.current,
		};

		const response = await smartChat(
			messagesToSend,
			lessonInfoRef.current.steps[step].type,
			lessonInfoRef.current.steps,
			step,
			exercise_info,
			add_to_log,
			abortControllerRef.current,
			pronunciation?.pronunciation_info
		);

		// Get the action from the response
		const { action_decision, text, information_update } = response;

		console.log("Action decision:", action_decision);
		console.log("Information update:", information_update);
		console.log("Response text:", text);

		// Check if the information update is in infoRef
		information_update &&
			infoHandlerRef.current &&
			infoHandlerRef.current(information_update);

		if (text) {
			handleSpeak(text);
		}

		// Perform action_decision
		parseAndExecuteAction(action_decision);

		/*if ((message && add_to_log)) { // || state === "start"
			handleSpeak(text);
		} else {
			setSuggestion(text);
		}*/

		return text;
	};

	return (
		<LiveTutorContext.Provider
			value={{
				isHovered,
				setIsHovered,
				showMessageLog,
				setShowMessageLog,
				showSettings,
				setShowSettings,

				suggestion,
				setSuggestion,
				speechState,
				setSpeechState,
				show,
				setShow,
				completed,

				messages,
				setMessages,
				liveTutorSettings,
				setLiveTutorSettings,
				handleSuggestion,
				handleMessageQueue,
				handleChat,
				systemMessageRef,
				handleSpeak,
				userMessageCallbackRef,
				messageCallbackRef,
				callbackRef,
				actionHandler,
				finishCallbackRef,
				stepFinishCallbackRef,
				infoHandlerRef,
				abortControllerRef,
				pronunciationResultsRef,

				currentState,
				setCurrentState,
				lessonInfoRef,
				step,
				setStep,
				showContinueButton,
				setShowContinueButton,

				exerciseInfoRef,
				activeStates,
				addActiveState,
				removeActiveState,

				resetLessons,
			}}
		>
			{<ControlButtons />}
			<MessageLogPanel />
			<SettingsModal />

			<div
				className={`
					tutorial-step-0
						  ${show ? "block" : "hidden"}
				
						  z-10
						  flex
						  items-end
						 
						
						  fixed        
						  mx-auto    
						  h-[40vh]
						  w-full
						  border-b-4 
						  border-black
						  bg-gradient-to-br from-gray-700 to-slate-700
						  md:bg-none
						  md:border-b-0 
						  md:fixed  
						  md:top-auto
						  md:left-auto
						  md:translate-x-0
						  md:translate-y-0
						  md:bottom-0
						  ${showMessageLog ? "md:right-[400px]" : "md:right-0"}
						  md:h-1/2
						  md:w-auto
					  `}
			>
				<Canvas
					style={{
						background: "transparent",
						// offset to the right by like 3 px
					}}
					camera={{
						position: [0, 0, 1.4],
						fov: 28,
						near: 0.1,
						far: 5,
						zoom: 1,
					}}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<ambientLight intensity={0.3} />
					<Character3D />
				</Canvas>
				<NotificationIcon />
				{
					<RecordingButton
						showContinueButton={showContinueButton}
						continueHandler={continueHandler}
					/>
				}

				<SpeechBubble />
			</div>
			<div className="h-[40vh]" />

			{children}
		</LiveTutorContext.Provider>
	);
};
interface ModelLoadingIndicatorProps {
	loadingLanguage: string | null;
}
const ModelLoadingIndicator = ({
	loadingLanguage,
}: ModelLoadingIndicatorProps) => {
	if (!loadingLanguage) return null;

	return (
		<div
			className="
			absolute
			bottom-0
			right-0
			sm:left-auto 
			sm:right-4 
			sm:bottom-4 
			md:bottom-auto 
			md:top-20 
			z-30 
			bg-white/90 
			dark:bg-gray-800/90 
			rounded-lg 
			shadow-lg 
			p-0
			md:p-4
			w-1/2
			md:w-auto
			flex 
			items-center 
			justify-center 
			sm:justify-start 
			space-x-2 
			sm:space-x-3 
			animateFadeIn


			
		"
		>
			<div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-500" />
			<span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 truncate">
				{`${loadingLanguage} model loading...`}
			</span>
		</div>
	);
};

// Separate components for each UI element
const NotificationIcon = () => {
	const { suggestion, handleSuggestion } = useLiveTutor();

	return suggestion ? (
		<button
			className="
		  absolute 
		  top-20
		  left-1/2
		  translate-x-[25px]
		  md:top-0
		  md:-translate-x-1/2 
		  md:left-auto 
		  md:right-20 
		  flex items-center justify-center 
		  bg-red-500 text-white font-bold 
		  rounded-full w-12 h-16 
		  text-sm z-20 
		  hover:scale-110 
		  transition-transform duration-200 
		  cursor-pointer"
			onClick={handleSuggestion}
		>
			<FaExclamation className="text-2xl" />
		</button>
	) : null;
};

const SpeechBubble = () => {
	const { speechState, setSpeechState, handleMessageQueue } = useLiveTutor();
	const { stopTextAsync } = useAzure();
	const { isOpen, currentStep, setDisabledActions } = useTour();
	const messageContainerRef = useRef(null);
	const activeMessageRef = useRef<HTMLParagraphElement | null>(null);

	// Auto-scroll effect
	useEffect(() => {
		if (activeMessageRef.current && messageContainerRef.current) {
			activeMessageRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [speechState.currentSpeechIndex]);

	// check if current messag is in add to log
	const message =
		speechState.messageQueue.length > 0 &&
		speechState.messageQueue[speechState.currentSpeechIndex];

	// fine for now as long as this is the .add_to_log is false only for the last message
	const speechBubbleOpen =
		message &&
		(!message.message_type ||
			(message.message_type && message.message_type !== "roleplay"));

	return speechBubbleOpen ? (
		<div
			className="absolute 
		left-1
		top-[25vh]
		max-h-[18vh]
		md:top-14
    	md:-translate-y-full
        md:left-auto 
        md:right-[150px]
        bg-white text-black 
        p-4 w-72 z-50 pb-2
        transition-all duration-300 
        ease-in-out rounded-lg 
        shadow-lg border border-gray-200 
        animate-fadeIn tutorial-step-1"
		>
			{/* Close button */}
			{!isOpen && (
				<button
					onClick={() => {
						stopTextAsync();
						setSpeechState({
							messageQueue: [],
							currentSpeechIndex: 0,
						});
					}}
					className="absolute top-[-12px] right-[-12px] bg-gray-100 text-gray-600 hover:bg-gray-200 
                    rounded-full w-7 h-7 flex items-center justify-center shadow-sm border border-gray-300 
                    transition-all duration-200 hover:scale-105"
				>
					<MdClose className="text-lg" />
				</button>
			)}

			<div
				className="max-h-[11vh] space-y-2 md:max-h-20 overflow-y-auto"
				ref={messageContainerRef}
			>
				{speechState.messageQueue
					.filter((message) => !message.message_type)
					.map((message: any, index: number) => (
						<P
							key={index}
							ref={
								index === speechState.currentSpeechIndex
									? activeMessageRef
									: null
							}
							className={`
								whitespace-normal break-words
								text-sm leading-relaxed ${
									index === speechState.currentSpeechIndex
										? "text-black font-medium"
										: "text-gray-400"
								}`}
							onClick={() => {
								if (isOpen) {
									setDisabledActions(false);
								}
							}}
						>
							{message.content}
						</P>
					))}
			</div>
			{speechState.currentSpeechIndex < speechState.messageQueue.length - 1 && (
				<div className="flex justify-end mt-2 z-20">
					<button
						onClick={handleMessageQueue}
						className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm z-20"
					>
						Next
					</button>
				</div>
			)}
			<div
				className="absolute w-4 h-4 bg-white transform border-r border-b border-gray-200 z-0
  -top-2 rotate-[225deg] right-24
  md:top-auto md:-bottom-2 md:rotate-45 md:right-8"
			></div>
		</div>
	) : null;
};

const ControlButtons = () => {
	const {
		setShowMessageLog,
		setShowSettings,
		show,
		setShow,
		suggestion,
		handleSuggestion,
	} = useLiveTutor();
	const { setCurrentStep, isOpen, setDisabledActions } = useTour();
	const { stopTextAsync } = useAzure();

	return (
		<div className="fixed z-20 flex gap-2 flex-col top-24 right-4 md:top-auto md:bottom-80 md:right-4 ">
			{show && (
				<button
					onClick={() => {
						setShowMessageLog((prev) => !prev);
						if (isOpen) {
							setCurrentStep((prev) => prev + 1);
						}
					}}
					className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors tutorial-step-3"
				>
					<MdChat className="text-xl" />
				</button>
			)}
			{show && (
				<button
					onClick={() => setShowSettings(true)}
					className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
				>
					<MdSettings className="text-xl" />
				</button>
			)}
		</div>
	);

	/*
	<button
				onClick={() => {
					if (!show && suggestion) {
						setShow(true);
						handleSuggestion();
					} else {
						if (show) {
							stopTextAsync();
						}

						setShow((prev) => !prev);
					}
				}}
				className={`p-3 rounded-full ${!show && suggestion ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
			>
				{!show && suggestion ? (
					<MdError className="text-xl" />
				) : show ? (
					<MdVisibilityOff className="text-xl" />
				) : (
					<MdVisibility className="text-xl" />
				)}
			</button>*/
};

const MessageLogPanel = () => {
	const { showMessageLog, setShowMessageLog, messages, handleChat } =
		useLiveTutor();
	const { isOpen, setIsOpen } = useTour();

	return showMessageLog ? (
		<div
			className={`fixed z-20
flex
flex-col
bg-white
rounded-lg
shadow-2xl
border border-gray-200
overflow-hidden
tutorial-step-4

/* Small screens (default) */
bottom-0
w-full

h-[calc(60vh)]

/* Large screens (md and up) */
md:top-auto
md:left-auto
md:right-4
md:h-[600px]
md:w-[400px]
`}
		>
			{/* Header */}
			<div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b">
				<h2 className="text-lg font-semibold text-gray-700">Message Log</h2>
				<button
					onClick={() => setShowMessageLog(false)}
					className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
				>
					<MdClose className="text-xl" />
				</button>
			</div>

			{/* Messages Container */}
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{messages
					.filter((message) => message.role !== "system")
					.map((message, index) => (
						<div
							key={index}
							className={`flex ${
								message.role === "user" ? "justify-end" : "justify-start"
							}`}
						>
							<div
								className={`
	max-w-[80%]
	p-3
	rounded-lg
	${
		message.role === "user"
			? "bg-blue-500 text-white rounded-br-none"
			: "bg-gray-100 text-gray-800 rounded-bl-none"
	}
  `}
							>
								<P className="text-sm whitespace-pre-wrap">{message.content}</P>
							</div>
						</div>
					))}
			</div>

			{/* Input Form */}
			<div className="p-4 bg-gray-50 border-t">
				<form
					className="flex items-center gap-2"
					onSubmit={(e: any) => {
						e.preventDefault();
						const input = e.target.elements.messageInput;
						const message = input.value.trim();
						if (message) {
							if (isOpen) {
								setIsOpen(false);
							}

							handleChat(message);
							input.value = "";
						}
					}}
				>
					<input
						autoComplete="off"
						type="text"
						name="messageInput"
						placeholder="Type your message..."
						className="flex-1 px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					/>
					<button
						type="submit"
						className="p-2.5 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
					>
						<MdSend className="text-xl" />
					</button>
				</form>
			</div>
		</div>
	) : null;
};

const SettingsModal = () => {
	const {
		showSettings,
		setShowSettings,
		liveTutorSettings,
		setLiveTutorSettings,
	} = useLiveTutor();

	return (
		<LiveTutorSettingsModal
			showSettings={showSettings}
			setShowSettings={setShowSettings}
			settings={liveTutorSettings}
			setSettings={(key, value) =>
				setLiveTutorSettings({ ...liveTutorSettings, [key]: value })
			}
		/>
	);
};

const RecordingButton = ({
	showContinueButton,
	continueHandler,
}: {
	showContinueButton: boolean;
	continueHandler: () => void;
}) => {
	const { user, transcribe, checkStatus } = useServer();
	const {
		abortControllerRef,
		currentState,
		activeStates,
		handleChat,
		handleSpeak,
		setSuggestion,
		setSpeechState,
		liveTutorSettings,
		exerciseInfoRef,
		lessonInfoRef,
		step,
		pronunciationResultsRef,
	} = useLiveTutor();
	const {
		startPronunciationAssessment,
		stopPronunciationAssessment,
		stopTextAsync,
	} = useAzure();
	const { showToast } = useToast();

	const vadRef = useRef<any>(null);
	const [loadingLanguage, setLoadingLanguage] = useState<string | null>(null);
	const [isThinking, setIsThinking] = useState(false);

	const [isRecording, setIsRecording] = useState(false);
	const recorderRef = useRef<AudioRecorder | null>(null);
	const resultsRef = useRef<any>({
		resolve: null,
		sentences: [],
		words: [],
		pronunciation_info: null,
	});
	const pronunciationPromise = useRef<Promise<any> | null>(null);

	const discardRecord = async () => {
		if (recorderRef.current && recorderRef.current.isRecording()) {
			await recorderRef.current.stop();
			recorderRef.current.clear();
			setIsRecording(false);
		}
	};

	const startRecord = async () => {
		if (activeStates.current.includes("pronunciation")) {
			if (!exerciseInfoRef.current.text) {
				console.error("Text not set to pronounce in exercise info");
				return;
			}

			const callbacks = {} as RecognitionCallbacks;

			callbacks.recognizing = (s: any, e: any) => {
				console.log(`${e.result.text}`);

				// set promise that recognized will come soon after
				pronunciationPromise.current = new Promise((resolve) => {
					resultsRef.current.resolve = resolve;
				});
			};

			callbacks.recognized = (s: any, e: any) => {
				if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
					console.log(`RECOGNIZED: Text=${e.result.text}`);
				} else if (e.result.reason === sdk.ResultReason.NoMatch) {
					console.log("No speech could be recognized.");
					return;
				}

				const jsonResult = JSON.parse(
					e.result.properties.getProperty(
						sdk.PropertyId.SpeechServiceResponse_JsonResult
					)
				);

				const { NBest } = jsonResult;

				// Process NBest results
				if (NBest && NBest.length > 0) {
					const result = NBest[0];

					// Saving this for the pronunciation component
					resultsRef.current.pronunciation_info = { words: result.Words };

					// Create sentence object
					const sentence = {
						text: result.Display,
						words: [] as any[],
					} as any;

					// Add sentence level scores if below threshold
					if (
						result.PronunciationAssessment &&
						result.Display.split(" ").length > 1
					) {
						const { ProsodyScore, FluencyScore } =
							result.PronunciationAssessment;

						if (ProsodyScore < 50) {
							sentence.prosody = ProsodyScore;
						}

						if (FluencyScore < 50) {
							sentence.fluency = FluencyScore;
						}
					}

					// Process words
					if (result.Words) {
						result.Words.forEach((word: any) => {
							const wordObj = {
								text: word.Word,
								accuracy: word.PronunciationAssessment.AccuracyScore,
								phonemes: [] as any[],
							};

							let hasProblem = false;

							// iterate the phonemes
							if (word.Phonemes) {
								word.Phonemes.forEach((phoneme: any) => {
									if (phoneme.PronunciationAssessment?.AccuracyScore < 50) {
										wordObj.phonemes.push({
											phone: phoneme.Phoneme,
											score: phoneme.PronunciationAssessment.AccuracyScore,
										});
										hasProblem = true;
									}
								});
							}

							if (hasProblem) {
								sentence.words.push(wordObj);
							}
						});
					}

					resultsRef.current.sentences.push(sentence);

					/*
						// Only add sentence if it has prosody/fluency issues or problematic words
					if (
						sentence.prosody ||
						sentence.fluency ||
						sentence.words.length > 0
					) {
						
					}*/
				}

				// Tell the promise that we have results
				if (resultsRef.current.resolve) {
					resultsRef.current.resolve();
				}
			};

			callbacks.results = (words: any, scores: any, recordedBlob: any) => {
				pronunciationResultsRef.current = {
					...pronunciationResultsRef.current,
					words,
					scores,
				};
			};

			console.log(
				"Starting pronunciation assessment",
				exerciseInfoRef.current.text
			);
			startPronunciationAssessment(exerciseInfoRef.current.text, callbacks);
		}

		try {
			if (!recorderRef.current) {
				recorderRef.current = new AudioRecorder();
				await recorderRef.current.init();
			}

			recorderRef.current.start();
		} catch (error) {
			console.error("Error starting recording:", error);
		}
	};

	const stopRecord = async () => {
		if (recorderRef.current && recorderRef.current.isRecording()) {
			const blob = await recorderRef.current.stop();

			if (blob) {
				try {
					// Set timeout to 3 seconds and call stopPronunciationAssessment if it times out

					const result = await Promise.race([
						pronunciationPromise.current,
						new Promise((resolve) =>
							setTimeout(() => resolve("timeout"), 3000)
						),
					]);

					pronunciationResultsRef.current = {
						...pronunciationResultsRef.current,
						recordedBlob: blob,
					};

					stopPronunciationAssessment();
					pronunciationPromise.current = null;

					setIsThinking(true);

					let message = "" as any;
					try {
						message = await transcribe(blob, user.nativeLanguage);
					} catch (error) {
						if (activeStates.current.includes("pronunciation")) {
							message = resultsRef.current.sentences
								.map((sentence: any) => sentence.text)
								.join(" ");
						} else {
							throw error;
						}
					}

					const pronunciation = activeStates.current.includes("pronunciation")
						? {
								pronunciation_analysis: resultsRef.current.sentences,
								pronunciation_info: resultsRef.current.pronunciation_info,
						  }
						: null;

					console.log(pronunciation);

					await handleChat(message, true, pronunciation);
				} catch (error: any) {
					if (error.message === "Model is loading...") {
						// Start the polling & UI update
						setLoadingLanguage(user.nativeLanguage);
						handleSpeak(
							`It looks like you spoke in ${user.nativeLanguage}. I need a minute to load my translation model so I can understand you.`
						);
					} else {
						showToast("Error transcribing audio");
					}
				} finally {
					resultsRef.current.sentences = [];
					setIsThinking(false);
				}
			}
		}
	};

	useEffect(() => {
		const initializeVAD = async () => {
			try {
				if (typeof window.vadit === "function") {
					window
						.vadit(
							() => setIsRecording(true),
							() => setIsRecording(false)
						)
						.then((vad: any) => {
							console.log("VAD initialized");
							vadRef.current = vad;
						});
				}
			} catch (error) {
				console.error("Error initializing VAD:", error);
				// Handle the error (e.g., show an error message to the user)
			}
		};
		initializeVAD();
		return () => {
			if (vadRef.current) {
				vadRef.current.pause();
			}
		};
	}, []);

	useEffect(() => {
		if (vadRef.current) {
			if (liveTutorSettings.liveConversation) {
				//console.log("Starting VAD");
				vadRef.current.start();
			} else {
				//console.log("Stopping VAD");
				vadRef.current.pause();
			}
		}
	}, [liveTutorSettings.liveConversation]);

	useEffect(() => {
		// clear everything
		if (isRecording) {
			setSuggestion(null);
			setSpeechState({
				messageQueue: [],
				currentSpeechIndex: 0,
			});

			stopTextAsync();
			startRecord();
			if (
				abortControllerRef.current &&
				!abortControllerRef.current.signal.aborted
			) {
				abortControllerRef.current.abort();
				abortControllerRef.current = new AbortController();
			}
		} else {
			stopRecord();
		}
	}, [isRecording]);

	useEffect(() => {
		let pollInterval: NodeJS.Timeout;

		const pollStatus = async () => {
			if (loadingLanguage) {
				const status = await checkStatus(loadingLanguage);
				if (status === "running") {
					showToast(
						`${loadingLanguage} model loaded successfully. You can now speak in ${loadingLanguage}.`
					);
					setLoadingLanguage(null);
				}
			}
		};

		if (loadingLanguage) {
			pollStatus(); // Check immediately
			pollInterval = setInterval(pollStatus, 30000); // Then every 30s
		}

		return () => {
			if (pollInterval) {
				clearInterval(pollInterval);
			}
		};
	}, [loadingLanguage]);

	return (
		<div
			className={`fixed ${
				currentState === "roleplay" ? "bottom-4" : "bottom-4"
			} md:bottom-8 left-1/2 -translate-x-1/2 z-20 tutorial-step-2`}
		>
			<ModelLoadingIndicator loadingLanguage={loadingLanguage} />

			<div className="flex items-center gap-6">
				{isRecording && (
					<button
						onClick={discardRecord}
						className="p-6 rounded-full text-white bg-gray-500 transition-all duration-300 
							border-2 border-gray-400 shadow-lg"
					>
						<div
							className="relative flex items-center justify-center w-4 h-4 md:w-16 md:h-16 rounded-full 
							bg-gray-500 border border-gray-400"
						>
							<MdDelete className="absolute text-2xl md:text-5xl text-white" />
						</div>
					</button>
				)}

				{showContinueButton ? (
					<button
						onClick={continueHandler}
						className={`transform transition-all duration-300 px-8 py-4 rounded-full text-white 
							  border-2 shadow-lg 
							flex items-center gap-2 animate-morphToContinue
							${
								step < lessonInfoRef.current.steps.length - 1
									? "bg-green-500 border-green-400"
									: "bg-orange-500 border-orange-400"
							}
							`}
					>
						<span className="text-lg md:text-xl font-semibold">
							{step < lessonInfoRef.current.steps.length - 1
								? "Continue"
								: "Finish"}
						</span>
						<MdArrowForward className="text-xl md:text-2xl" />
					</button>
				) : (
					<button
						onClick={() => !isThinking && setIsRecording(!isRecording)}
						className={`p-6 rounded-full text-white transition-all duration-300 shadow-xl border-2  border-gray-700
							transform ${
								isRecording
									? "bg-red-500"
									: isThinking
									? "bg-blue-400 cursor-not-allowed"
									: "bg-blue-500"
							}`}
					>
						{isRecording ? (
							<div
								className="relative flex items-center justify-center w-4 h-4 md:w-16 md:h-16 rounded-full 
								bg-red-500 border border-red-400"
							>
								<MdStop className="absolute text-2xl md:text-5xl text-white" />
							</div>
						) : isThinking ? (
							<div
								className="relative flex items-center justify-center w-4 h-4 md:w-16 md:h-16 rounded-full 
								bg-blue-400 border border-blue-300"
							>
								<div className="absolute w-full h-full animate-spin">
									<div
										className="h-full w-full rounded-full border-4 border-t-transparent 
										border-blue-600 animate-spin"
									></div>
								</div>
								<div
									className="absolute w-2/3 h-2/3 rounded-full border-4 border-l-transparent 
									border-blue-500 animate-spin-reverse"
								></div>
							</div>
						) : (
							<div
								className="relative flex items-center justify-center w-8 h-8 md:w-16 md:h-16 rounded-full 
								bg-blue-500"
							>
								<MdMic className="absolute text-4xl md:text-5xl text-white" />
							</div>
						)}
					</button>
				)}
			</div>
		</div>
	);
};
export default LiveTutorProvider;
